import * as moment from "moment-timezone";
import React, {
  useEffect,
  useState,
  useCallback
} from "react";

import useDrop from "src/core/drops/hooks/useDrop";
import {
  INotification,
  useNotificationsApi,
  NotificationType
} from "src/core/notifications/api/notifications.api";
import useCache from "src/core/shared/hooks/useCache";

export interface UseSmartNotificationReturnType {
  isLoading: boolean,
  showSmartNotification: boolean,
  currentNotification: INotification | null,
  handleDismissNotification: () => void,
  handleMarkAsRead: () => void,
  setShowSmartNotification: React.Dispatch<React.SetStateAction<boolean>>
}

export const useSmartNotifications = (userId: number | null): UseSmartNotificationReturnType => {
  const [ currentNotification, setCurrentNotification ] = useState<INotification | null>(null);
  const [ getSmartNotificationsResponse, getSmartNotifications ] = useNotificationsApi("GET_USER_SMART_NOTIFICATIONS");
  const [ , markAsRead ] = useNotificationsApi("MARK_NOTIFICATION_AS_READ");
  const [ dismissNotificationResponse, dismissNotification ] = useNotificationsApi("DISMISS_NOTIFICATION");
  const [ showSmartNotification, setShowSmartNotification ] = useState(false);
  const [ currentUserId, setCurrentUserId ] = useState<number | null>(null);
  const { removeItemByKey } = useCache();
  const [ { drop } ] = useDrop();
  const isLoading = getSmartNotificationsResponse.pending;

  // On mount, get smart notifications
  useEffect(() => {
    if (drop?.id && (userId && currentUserId !== userId)) {
      getSmartNotifications({
        userId,
        branchId: drop?.id
      });
    }
  }, [
    currentUserId,
    drop,
    getSmartNotifications,
    userId
  ]);

  // anytime we get up-to-date smart notifications, display if notifications exist
  useEffect(() => {
    if (getSmartNotificationsResponse.data) {
      if (getSmartNotificationsResponse.data.length > 0) {
        const currentNotificationObject: INotification = getSmartNotificationsResponse.data[ 0 ];

        if (currentNotificationObject?.notificationUsers) {
          // used to stop multiple requests if userId already set
          setCurrentUserId(currentNotificationObject.notificationUsers[ 0 ].userId);

          if (currentNotificationObject.type === NotificationType.ORDER_REMINDER) {
            // update title to show how long before today's cut off
            const expiresAt = moment.utc(currentNotificationObject.notificationUsers[ 0 ].expiresAt);
            const hours = expiresAt.diff(moment.utc().toDate(), "hours");
            const timeLeftString = `less than ${hours} ${hours > 1 ? "hours" : "hour"}`;

            currentNotificationObject.title = `It's ${timeLeftString} before cutoff today. ${currentNotificationObject.title}.`;
          }
        }

        setCurrentNotification(currentNotificationObject);
        setShowSmartNotification(true);
      }

      if (getSmartNotificationsResponse.data.length === 0) {
        setShowSmartNotification(false);
      }
    }
  }, [ getSmartNotificationsResponse ]);

  // if notification dismissed successfully, get up-to-date smart notifications
  useEffect(() => {
    if (dismissNotificationResponse.data && drop && userId) {
      removeItemByKey(`smartNotifications-${userId}-${drop.id}`);

      getSmartNotifications({
        userId,
        branchId: drop.id
      });
      dismissNotificationResponse.data = null;
    }
  }, [
    dismissNotificationResponse,
    drop,
    getSmartNotifications,
    removeItemByKey,
    userId
  ]);

  // update notification as dismissed
  const handleDismissNotification = useCallback(() => {
    if (currentNotification && userId) {
      dismissNotification({
        notificationId: currentNotification.id,
        userId
      });
    }
  }, [
    currentNotification,
    dismissNotification,
    userId
  ]);

  // update notification as read
  const handleMarkAsRead = useCallback(() => {
    if (currentNotification && userId) {
      markAsRead({
        notificationId: currentNotification.id,
        userId
      });
    }
  }, [
    currentNotification,
    userId,
    markAsRead
  ]);

  return {
    isLoading,
    showSmartNotification,
    currentNotification,
    handleDismissNotification,
    handleMarkAsRead,
    setShowSmartNotification
  };
};
